import { howItIsHeld, NEEDS_AT_LEAST, whoMade } from "@rally/agreed";
import { Alert, Button, Col, SettingRow, Spinner, Stack, Text, useConfirm } from "@rally/ui-kit";
import { useCallback, useEffect, useState } from "react";
import { howLongAgo } from "../localization/format.ts";
import { say } from "../localization/say.ts";
import { act, addADevice, read } from "../net/rally.ts";
import { why } from "../net/why.ts";
import { reloadNow } from "../update.ts";
import { KeyRow } from "./key-row.tsx";

type MyDevice = {
  credentialId: string;
  attachment?: string;
  transports?: string[];
  aaguid?: string;
  called?: string;
  lastUsedAt?: string;
  enrolledAt?: string;
};

function lastly(device: MyDevice): string {
  if (device.lastUsedAt) return say("keys.used")(howLongAgo(device.lastUsedAt));
  if (device.enrolledAt) return say("keys.added")(howLongAgo(device.enrolledAt));
  return say("keys.neverUsed");
}

export function MyKeys({ onLeave }: { onLeave: () => void }) {
  const [devices, setDevices] = useState<MyDevice[] | null>(null);
  const [wrong, setWrong] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [ending, setEnding] = useState(false);
  const { ask, dialog } = useConfirm();
  const [me, setMe] = useState<{ account: string; handle: string } | null>(null);

  const load = useCallback(() => {
    read<{ account: string; handle: string; devices: MyDevice[] }>("my.devices", {})
      .then((said) => {
        setDevices(said.devices);

        setMe({ account: said.account, handle: said.handle });
      })
      .catch((cause) => setWrong(why(cause, "add")));
  }, []);
  useEffect(load, [load]);

  async function call(credentialId: string, called: string) {
    setDevices(
      (was) => was?.map((one) => (one.credentialId === credentialId ? { ...one, called } : one)) ?? was,
    );
    try {
      await act("passkey.call", { credentialId, called });
    } catch (cause) {
      setWrong(why(cause, "add"));
      load();
    }
  }

  async function addOne() {
    if (!me) return;
    setAdding(true);
    setWrong(null);
    try {
      await addADevice(me);
      load();
    } catch (cause) {
      setWrong(why(cause, "add"));
    } finally {
      setAdding(false);
    }
  }

  async function drop(credentialId: string) {
    const sure = await ask({
      title: say("keys.removeTitle"),
      description: say("keys.removeMeans"),
      confirmLabel: say("keys.remove"),
      cancelLabel: say("keys.keep"),
      tone: "danger",
    });
    if (!sure) return;
    try {
      await act("passkey.remove", { credentialId });
      load();
    } catch (cause) {
      setWrong(why(cause, "add"));
    }
  }

  async function endAll() {
    const sure = await ask({
      title: say("keys.endAll"),
      description: say("keys.endAllWhy"),
      confirmLabel: say("keys.endAll"),
      cancelLabel: say("providers.cancel"),
      tone: "danger",
    });
    if (!sure) return;
    setEnding(true);
    setWrong(null);
    try {
      await act("session.endAll", {});
      reloadNow();
    } catch (cause) {
      setWrong(why(cause, "add"));
      setEnding(false);
    }
  }

  if (!devices) return <Spinner label={say("keys.loading")} />;

  return (
    <Stack gap={3}>
      {dialog}
      <Text typeName="body" color="text.secondary">
        {say("keys.why")}
      </Text>
      {wrong ? <Alert tone="danger">{wrong}</Alert> : null}
      <Stack gap={2}>
        {devices.map((device) => (
          <KeyRow
            key={device.credentialId}
            testID={`my-key-${device.credentialId.slice(0, 8)}`}
            done
            label={whoMade(device)}
            hint={`${lastly(device)}${howItIsHeld(device) ? `, ${howItIsHeld(device)}` : ""}`}
            called={device.called ?? ""}
            onCalled={(said) => call(device.credentialId, said)}
            removable={devices.length > NEEDS_AT_LEAST}
            onRemove={() => drop(device.credentialId)}
          />
        ))}
        <KeyRow
          testID="add-key"
          done={false}
          ready={Boolean(me) && !adding}
          waiting={adding}
          spare
          label={say("join.keyMore")}
          hint={say("keys.addWhere")}
          called=""
          onCalled={() => {}}
          onRemove={() => {}}
          onAdd={addOne}
        />
      </Stack>
      <Stack gap={2}>
        <Text typeName="label" color="text.secondary">
          {say("keys.sessions")}
        </Text>
        <Col>
          <SettingRow
            title={say("keys.thisDevice")}
            note={say("keys.endHereWhy")}
            control={
              <Col style={{ alignItems: "flex-end" }}>
                <Button variant="outline" size="sm" icon="logout" onPress={onLeave} testID="keys-end-here">
                  {say("keys.signOut")}
                </Button>
              </Col>
            }
          />
          <SettingRow
            title={say("keys.everyDevice")}
            note={say("keys.endAllWhy")}
            last
            control={
              <Col style={{ alignItems: "flex-end" }}>
                <Button
                  variant="danger"
                  size="sm"
                  icon="logout"
                  loading={ending}
                  onPress={() => void endAll()}
                  testID="keys-end-all"
                >
                  {say("keys.signOut")}
                </Button>
              </Col>
            }
          />
        </Col>
      </Stack>
    </Stack>
  );
}
